import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  api, post, setTokens, clearTokens, getRefreshToken, setAccessToken, refreshSession, onAuthChange,
} from './api.js';
import { loadPhoto } from './photos.js';

const AuthContext = createContext(null);

/** Sign out after this long with no pointer, key, or tab activity. */
const IDLE_MS = 60 * 60 * 1000;
const ACTIVITY_KEY = 'peopleos.lastActivity';
const LAST_USER_KEY = 'peopleos.lastUser';
const LAST_PHOTO_KEY = 'peopleos.lastPhoto';
const LAST_PHOTO_MAX = 220 * 1024;

export function readLastUser() {
  try {
    const raw = window.localStorage.getItem(LAST_USER_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u?.id || !u.fullName) return null;
    return u;
  } catch {
    return null;
  }
}

export function readLastPhoto() {
  try {
    const v = window.localStorage.getItem(LAST_PHOTO_KEY);
    return v && v.startsWith('data:image/') ? v : null;
  } catch {
    return null;
  }
}

function rememberLastUser(user) {
  if (!user?.id) return;
  try {
    window.localStorage.setItem(LAST_USER_KEY, JSON.stringify({
      id: user.id,
      fullName: user.fullName,
      firstName: user.firstName ?? null,
      email: user.email ?? '',
      hasPhoto: Boolean(user.photoUrl),
    }));
  } catch { /* private browsing */ }

  if (!user.photoUrl) {
    try { window.localStorage.removeItem(LAST_PHOTO_KEY); } catch { /* ignore */ }
    return;
  }

  loadPhoto(user.id, true).then(async (url) => {
    if (!url) {
      try { window.localStorage.removeItem(LAST_PHOTO_KEY); } catch { /* ignore */ }
      return;
    }
    try {
      const blob = await fetch(url).then((r) => r.blob());
      if (!blob.type.startsWith('image/') || blob.size > LAST_PHOTO_MAX) return;
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      if (typeof dataUrl === 'string') window.localStorage.setItem(LAST_PHOTO_KEY, dataUrl);
    } catch { /* ignore */ }
  });
}

function readActivity() {
  try {
    const n = Number(window.localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function touchActivity() {
  try { window.localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch { /* private browsing */ }
}

function idleExceeded() {
  const last = readActivity();
  if (!last) return false;
  return Date.now() - last >= IDLE_MS;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | authenticated | anonymous
  // Set while an administrator is viewing as somebody else. Held only in
  // memory, so a reload ends it — the stored refresh token is still theirs.
  const [viewingAs, setViewingAs] = useState(null);

  const loadSession = useCallback(async () => {
    try {
      const { data } = await api('/auth/me');
      setUser(data.user);
      setPermissions(data.permissions ?? []);
      setStatus('authenticated');
    } catch {
      clearTokens();
      setUser(null);
      setPermissions([]);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    // A stored refresh token means the last session may still be good; the /me
    // call proves it, because the server re-checks the employee is still active.
    // An hour of no activity still ends it even if the refresh token is valid.
    if (!getRefreshToken()) {
      setStatus('anonymous');
      return;
    }
    if (idleExceeded()) {
      clearTokens();
      try { window.localStorage.removeItem(ACTIVITY_KEY); } catch { /* ignore */ }
      setUser(null);
      setPermissions([]);
      setStatus('anonymous');
      return;
    }
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    return onAuthChange((token) => {
      if (token || getRefreshToken()) return;
      setViewingAs(null);
      setUser(null);
      setPermissions([]);
      setStatus((current) => (current === 'loading' ? current : 'anonymous'));
    });
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data } = await post('/auth/login', { email, password });
    setTokens(data);
    touchActivity();
    setUser(data.user);
    setPermissions(data.permissions ?? []);
    setStatus('authenticated');
    return data;
  }, []);

  /* The same session, arrived at differently. Everything after the exchange is
     identical to a password sign-in, so a Google session is not a second class
     of session with its own rules to keep in step. */
  const signInWithGoogle = useCallback(async (credential) => {
    const { data } = await post('/auth/google', { credential });
    setTokens(data);
    touchActivity();
    setUser(data.user);
    setPermissions(data.permissions ?? []);
    setStatus('authenticated');
    return data;
  }, []);

  const signOut = useCallback(async () => {
    setViewingAs(null);
    try { await post('/auth/logout', { refreshToken: getRefreshToken() }); } catch { /* leaving anyway */ }
    clearTokens();
    try { window.localStorage.removeItem(ACTIVITY_KEY); } catch { /* ignore */ }
    setUser(null);
    setPermissions([]);
    setStatus('anonymous');
  }, []);

  /**
   * Look at the application as somebody else.
   *
   * The administrator's own refresh token is deliberately left in place: this
   * swaps the short-lived access token only, so returning to their own account
   * is a refresh rather than a second sign-in, and a page reload ends the
   * session on its own. The server issues no refresh token for it and refuses
   * every write, so nothing here is load-bearing for safety — it is the
   * convenience half of a decision made on the other side.
   */
  const viewAs = useCallback(async (employee) => {
    const { data } = await post(`/auth/impersonate/${employee.id}`, {});
    setAccessToken(data.accessToken);
    setViewingAs(data.viewingAs);
    await loadSession();
    return data.viewingAs;
  }, [loadSession]);

  const stopViewingAs = useCallback(async () => {
    setViewingAs(null);
    // Their own refresh token never left, so this restores the real session.
    await refreshSession();
    await loadSession();
  }, [loadSession]);

  /**
   * Idle sign-out. Activity is any pointer, key, scroll or the tab becoming
   * visible again — not network noise from React Query, or a locked laptop
   * would never count as idle.
   */
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  useEffect(() => {
    if (status !== 'authenticated') return undefined;

    touchActivity();
    let lastTouch = 0;
    const bump = () => {
      const now = Date.now();
      if (now - lastTouch < 15_000) return;
      lastTouch = now;
      touchActivity();
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (idleExceeded()) signOutRef.current();
      else bump();
    };

    const tick = () => {
      if (idleExceeded()) signOutRef.current();
    };

    const events = ['pointerdown', 'keydown', 'click', 'scroll', 'touchstart'];
    for (const e of events) window.addEventListener(e, bump, { passive: true });
    document.addEventListener('visibilitychange', onVisible);
    const id = window.setInterval(tick, 30_000);
    tick();

    return () => {
      for (const e of events) window.removeEventListener(e, bump);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(id);
    };
  }, [status]);

  useEffect(() => {
    if (status === 'authenticated' && user && !viewingAs) rememberLastUser(user);
  }, [status, user, viewingAs]);

  const value = useMemo(() => ({
    user,
    permissions,
    status,
    viewingAs,
    viewAs,
    stopViewingAs,
    signIn,
    signInWithGoogle,
    signOut,
    refresh: loadSession,
    // Mirrors the server matrix, so the UI never offers an action that would 403.
    can: (permission) => permissions.includes(permission),
  }), [user, permissions, status, viewingAs, viewAs, stopViewingAs, signIn, signInWithGoogle, signOut, loadSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
